import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Github, ExternalLink, Loader2, Calendar, Tag } from 'lucide-react';
import { loadAllProjects } from '../lib/projects';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const ProjectDetailsPage = () => {
  const { id: slug } = useParams(); // Using 'id' parameter as 'slug'
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadProject = async () => {
      setLoading(true);
      try {
        const allProjects = await loadAllProjects();
        const projectData = allProjects.find(p => p.slug === slug);

        if (projectData) {
          setProject(projectData);
          // Update SEO elements
          document.title = `${projectData.title} | Maged's Portfolio`;
          
          // Try to update meta description
          let metaDescription = document.querySelector('meta[name="description"]');
          if (!metaDescription) {
            metaDescription = document.createElement('meta');
            metaDescription.name = 'description';
            document.head.appendChild(metaDescription);
          }
          metaDescription.content = projectData.description.substring(0, 160);
        } else {
          setError('Project not found');
        }
      } catch (err) {
        console.error('Error loading project details:', err);
        setError('Failed to load project details');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      loadProject();
    }
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-blue-500 mb-4" />
        <p className="text-muted-foreground text-lg">Loading project details...</p>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center p-4 text-center">
        <h1 className="text-3xl font-bold mb-4">Oops!</h1>
        <p className="text-muted-foreground mb-8">{error || 'Project not found'}</p>
        <Button onClick={() => navigate('/')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Portfolio
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navigation />
      
      <main className="flex-grow pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Back Button */}
            <Button 
              variant="ghost" 
              onClick={() => navigate('/#projects')}
              className="mb-8"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Projects
            </Button>

            {/* Project Header */}
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Badge variant="outline" className="capitalize">
                  {project.category}
                </Badge>
                {project.createdAt && (
                  <span className="text-sm text-muted-foreground flex items-center">
                    <Calendar className="w-3 h-3 mr-1" />
                    {new Date(project.createdAt).getFullYear()}
                  </span>
                )}
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold mb-6">{project.title}</h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                {project.description}
              </p>
            </div>

            {/* Project Image */}
            <div className="relative rounded-xl overflow-hidden border mb-12 shadow-lg">
              <div className="aspect-video">
                <img 
                  src={project.image} 
                  alt={project.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Project Details Grid */}
            <div className="grid md:grid-cols-3 gap-8">
              {/* Main Content */}
              <div className="md:col-span-2 space-y-8">
                <section>
                  <h2 className="text-2xl font-semibold mb-4">About the Project</h2>
                  <div className="prose dark:prose-invert max-w-none text-muted-foreground">
                    <p>
                      {project.description}
                    </p>
                    {/* If you add rich content fields to Supabase in the future, render them here */}
                  </div>
                </section>
              </div>

              {/* Sidebar Info */}
              <div className="space-y-8">
                {/* Tech Stack */}
                <Card className="p-6">
                  <h3 className="font-semibold mb-4 flex items-center">
                    <Tag className="w-4 h-4 mr-2" />
                    Technologies
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {project.technologies?.map((tech) => (
                      <Badge key={tech} variant="secondary">
                        {tech}
                      </Badge>
                    ))}
                  </div>
                </Card>

                {/* Links */}
                <Card className="p-6">
                  <h3 className="font-semibold mb-4">Links</h3>
                  <div className="space-y-3">
                    {project.github && (
                      <Button className="w-full justify-start" variant="outline" asChild>
                        <a href={project.github} target="_blank" rel="noopener noreferrer">
                          <Github className="w-4 h-4 mr-2" />
                          View Source Code
                        </a>
                      </Button>
                    )}
                    {project.demo && (
                      <Button className="w-full justify-start" asChild>
                        <a href={project.demo} target="_blank" rel="noopener noreferrer">
                          <ExternalLink className="w-4 h-4 mr-2" />
                          Live Demo
                        </a>
                      </Button>
                    )}
                  </div>
                </Card>
              </div>
            </div>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

// Mock Card component since we're using it in the sidebar
const Card = ({ children, className }) => (
  <div className={`bg-card text-card-foreground rounded-lg border shadow-sm ${className || ''}`}>
    {children}
  </div>
);

export default ProjectDetailsPage;
